import { useMemo } from "react";

import { ExpandedTooltip, Row, Text, TextVariant, TooltipSection } from "@bridgesplit/ui";
import {
    ActionType,
    FarmingInfoExpanded,
    getVaultNetApy,
    isVaultTemporarilyUnavailable,
    LendingVaultExpanded,
    LoopscalePointsAction,
    useFarmingInfoByActionTypeAndAddress,
    useLatestCollateralYieldHistoryByMint,
    useOraclePrices
} from "@bridgesplit/abf-react";

import { LendVaultAdditionalRewardsDisplay, RateDisplay } from "./common";
import { LendVaultBaseApy } from "./BaseApy";
import { RewardsDailyPoints } from "./daily-rewards";
import { RewardsProtocolPoints } from "./RewardsProtocolPoints";

export function RewardsLendVaultApy({
    vault,
    variant = "body1",
    collateralYields: collateralYieldsProp
}: {
    vault: LendingVaultExpanded | undefined;
    variant?: TextVariant;
    collateralYields?: Record<string, number>;
}) {
    const { farmingInfo, hasFarmingInfo } = useFarmingInfoByActionTypeAndAddress(vault?.vault.address, ActionType.Lend);
    const principalMint = vault?.vault.principalMint;
    const { getUsdPrice } = useOraclePrices([principalMint]);
    const { data: fetchedYields } = useLatestCollateralYieldHistoryByMint(
        !collateralYieldsProp && principalMint ? [principalMint] : []
    );
    const collateralYields = collateralYieldsProp ?? fetchedYields;

    const sections = useMemo(() => {
        return getLendVaultRewardSections(vault, farmingInfo, hasFarmingInfo);
    }, [vault, farmingInfo, hasFarmingInfo]);

    if (isVaultTemporarilyUnavailable(vault)) return <Text color="disabled">—</Text>;

    return (
        <ExpandedTooltip sections={sections}>
            <Row spacing={1}>
                <LendVaultAdditionalRewardsDisplay vault={vault} farmingInfo={farmingInfo} />
                <RateDisplay
                    apy={getVaultNetApy(vault, getUsdPrice, collateralYields)}
                    variant={variant}
                    color="body"
                />
            </Row>
        </ExpandedTooltip>
    );
}

function getLendVaultRewardSections(
    vault: LendingVaultExpanded | undefined,
    farmingInfo: FarmingInfoExpanded | undefined,
    hasFarmingInfo: boolean
): TooltipSection[] {
    const sections: TooltipSection[] = [];

    sections.push({
        type: "render",
        render: () => <LendVaultBaseApy vault={vault} />,
        px: 1.5,
        spacing: 0.5
    });

    sections.push({ type: "divider" });

    sections.push({
        type: "render",
        render: () => (
            <RewardsDailyPoints
                loopscalePointsAction={LoopscalePointsAction.LiquidStake}
                identifier={vault?.vault.address}
            />
        ),
        px: 1.5,
        spacing: 0.5
    });

    if (hasFarmingInfo) {
        sections.push({ type: "divider" });
        sections.push({
            type: "render",
            render: () => <RewardsProtocolPoints farmingInfo={farmingInfo} />,
            px: 1.5,
            spacing: 0.5
        });
    }

    return sections;
}
